import { defineStore } from 'pinia';
import { ref } from 'vue';

import { api } from '@/api/client';

// Shapes mirror crates/hq-controlplane/src/email_triage_api.rs +
// email_endpoint_admin.rs. Only the fields the UI needs are typed.

export type HandlingState = 'handled' | 'awaiting_review' | 'unrouted' | 'review_reply';

export interface ActivityRow {
  email_message_id: string;
  direction: 'inbound' | 'outbound';
  handling_state: HandlingState;
  from_addr: string;
  to_addrs: string[];
  subject: string | null;
  llm_category: string | null;
  llm_summary: string | null;
  agent_id: string | null;
  endpoint_id: string | null;
  endpoint_localpart: string | null;
  endpoint_plane: string | null;
  conversation_id: string | null;
  /** Attributed reviewer for a review_reply item; null = the admin Unassigned
   *  bucket. Only meaningful on the review_reply queue. */
  review_owner_id: string | null;
  occurred_at: string;
}

export interface QuarantineRow {
  id: string;
  from_addr: string;
  to_addr: string;
  reason: string;
  llm_category: string | null;
  llm_summary: string | null;
  sentio_message_id: string;
  created_at: string;
}

export interface ReviewCounts {
  awaiting_review: number;
  unrouted: number;
  quarantined: number;
  review_reply: number;
}

export interface ReviewQueues {
  awaiting_review: ActivityRow[];
  unrouted: ActivityRow[];
  quarantined: QuarantineRow[];
  /** Replies to the team / default sender. A user sees items attributed to
   *  them; an admin sees all, incl. the Unassigned (review_owner_id null) ones. */
  review_reply: ActivityRow[];
  counts: ReviewCounts;
}

interface BadgeResp {
  count: number;
  awaiting_review: number;
  unrouted: number;
  quarantined: number;
  review_reply: number;
}

/** Mirror of email_endpoint_admin::EndpointJson (read) + the editable config. */
export interface EmailEndpoint {
  id: string;
  localpart: string;
  kind: string;
  agent_id: string | null;
  instructions: string | null;
  plane: string;
  allow_escalation: boolean;
  sender_policy: string;
  reply_policy: string;
  default_reply_lang: string | null;
  spam_score_max: number | null;
  max_turns_per_sender_day: number | null;
  max_turns_per_day: number | null;
  allow_patterns: string[];
  enabled: boolean;
  /** True for the workspace outbound mailbox (HQ-managed): rendered as the
   *  workspace mailbox card, not a deletable/editable custom inbox. */
  is_system: boolean;
  message_count: number;
  needs_attention: number;
  last_occurred_at: string | null;
  // E7.2 disposition counters + reply-language distribution.
  received: number;
  replied: number;
  awaiting_review: number;
  unrouted: number;
  escalated: number;
  reply_languages: Record<string, number>;
  /** Court-document intake (docs/court-document-intake.md) is on for this inbox. */
  court_intake_enabled: boolean;
  /** A secure-mail password is stored (write-only; never returned). */
  court_secure_mail_password_set: boolean;
}

/** Create payload (email_endpoint_admin::CreateReq + flattened ConfigPatch). */
export interface EndpointCreate {
  localpart: string;
  /** Always 'mailbox' from the editor (a custom inbox). Agent addresses are
   *  auto-provisioned from the agent slug, not created here. */
  kind?: string;
  /** Required ONLY for Agent mode (plane='agent'). Omitted for Quick reply,
   *  which is agentless (the shared plane composes the reply). */
  agent_id?: string | null;
  instructions?: string | null;
  plane?: string;
  allow_escalation?: boolean;
  sender_policy?: string;
  reply_policy?: string;
  default_reply_lang?: string | null;
  spam_score_max?: number | null;
  max_turns_per_sender_day?: number | null;
  max_turns_per_day?: number | null;
  enabled?: boolean;
  allow_patterns?: string[];
  /** Court-document intake on/off (docs/court-document-intake.md). */
  court_intake_enabled?: boolean;
  /** Secure-mail password for the court portal. Write-only: sent only when the
   *  admin enters a new one; never returned by the API. */
  court_secure_mail_password?: string | null;
}

/** PATCH payload: everything except the immutable identity (localpart/kind). */
export type EndpointPatch = Partial<Omit<EndpointCreate, 'localpart' | 'kind'>>;

interface EndpointListResp {
  tenant_email_slug: string | null;
  domain: string | null;
  outbound_mailbox_localpart: string;
  endpoints: EmailEndpoint[];
}

/** An attachment listed on a viewed message. Metadata only - the bytes live in
 *  Sentio and are never stored on our side. Download (a streamed fetch through
 *  the gateway) is a separate follow-on; this read-only view just lists them. */
export interface EmailAttachment {
  /** Sentio's attachment id - the handle the download route resolves against. */
  id: string;
  filename: string;
  content_type: string;
  size: number;
}

/** Full content of one inbound email - headers + decoded bodies, fetched on
 *  demand from the raw EML in Sentio (never stored here), plus attachment
 *  metadata. Mirrors the gateway MessageContentJson shape verbatim. */
export interface EmailMessageContent {
  from: string[];
  to: string[];
  cc: string[];
  subject: string | null;
  date: string | null;
  text: string | null;
  html: string | null;
  attachments: EmailAttachment[];
}

/** PATCH payload for the workspace outbound mailbox. Fire-and-notify only:
 *  the only thing configurable is the address (localpart). Replies always file
 *  to Review + notify the initiator; the mailbox never auto-answers. */
export interface WorkspaceMailboxPatch {
  localpart?: string;
  /** Court-document intake on/off for the team mailbox (the address court
   *  secure-mail typically lands on). See docs/court-document-intake.md. */
  court_intake_enabled?: boolean;
  /** Secure-mail password for the court portal. Write-only: sent only when set;
   *  never returned by the API. */
  court_secure_mail_password?: string | null;
}

/** One email conversation (thread) for the Email > Threads tab. Mirrors the
 *  canonical ConvListItem shape from GET /v1/api/conversations - we only ever
 *  request channel_kind=email here, so the row reuses the same fields. */
export interface EmailThreadAgent {
  id: string | null;
  slug: string | null;
  display_name: string;
  avatar_url: string | null;
}
export interface EmailThread {
  id: string;
  channel_kind: string;
  last_activity_at: string;
  title: string | null;
  last_text: string | null;
  agent: EmailThreadAgent;
  artifact_count: number;
  /** Latest email message's headers (email threads only). */
  email_from?: string | null;
  email_to?: string[] | null;
  email_subject?: string | null;
}
interface ThreadListResp {
  conversations: EmailThread[];
  total: number;
}

function errOf(e: unknown): string {
  return (e as { error?: string }).error ?? 'something went wrong';
}

export const useEmailStore = defineStore('email', () => {
  const activity = ref<ActivityRow[]>([]);
  const activityTotal = ref(0);
  const threads = ref<EmailThread[]>([]);
  const threadsTotal = ref(0);
  const review = ref<ReviewQueues | null>(null);
  /** Member "Email replies" list for the active tab (to-review or reviewed). */
  const myReplies = ref<ActivityRow[]>([]);
  const endpoints = ref<EmailEndpoint[]>([]);
  const emailSlug = ref<string | null>(null);
  const domain = ref<string | null>(null);
  /** Localpart of the workspace outbound mailbox (default 'team'). */
  const outboundLocalpart = ref<string>('team');
  /** Nav-badge count: items needing a human (awaiting_review + unrouted + quarantined + review_reply). */
  const badge = ref(0);
  /** Member-facing count: replies attributed to the caller awaiting their review.
   *  Drives the member "Email replies" nav item (shown only when > 0). */
  const reviewReplyCount = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);

  /** Lightweight count for the nav badge; safe to call globally + often. */
  async function refreshBadge(): Promise<void> {
    try {
      const b = await api.get<BadgeResp>('/v1/api/email/badge');
      badge.value = b.count;
      reviewReplyCount.value = b.review_reply;
    } catch {
      // best-effort: a badge failure must never surface or block the nav.
    }
  }

  // Out-of-order guards: page/sort/tab changes fire these repeatedly, so an
  // older response must not overwrite a newer page's rows.
  let activityGen = 0;
  async function loadActivity(opts?: {
    page?: number;
    pageSize?: number;
    sort?: string;
    state?: string;
    endpointId?: string;
  }): Promise<void> {
    const gen = ++activityGen;
    loading.value = true;
    error.value = null;
    try {
      const size = opts?.pageSize ?? 25;
      const page = Math.max(1, opts?.page ?? 1);
      const params = new URLSearchParams({
        limit: String(size),
        offset: String((page - 1) * size),
        sort: opts?.sort ?? 'recent',
      });
      if (opts?.state) params.set('state', opts.state);
      if (opts?.endpointId) params.set('endpoint_id', opts.endpointId);
      // The endpoint currently returns a bare array (no total). A follow-on adds
      // {rows,total}; handle both so the UI doesn't break across the deploy.
      const r = await api.get<ActivityRow[] | { rows: ActivityRow[]; total: number }>(
        `/v1/api/email/activity?${params.toString()}`,
      );
      if (gen !== activityGen) return;
      if (Array.isArray(r)) {
        activity.value = r;
      } else {
        activity.value = r.rows;
        activityTotal.value = r.total;
      }
    } catch (e) {
      if (gen !== activityGen) return;
      error.value = errOf(e);
    } finally {
      if (gen === activityGen) loading.value = false;
    }
  }

  /** Email conversations as a thread list. Reuses the canonical conversation
   *  list endpoint with channel_kind=email; view=admin so an admin sees every
   *  email thread in the tenant (the API still scopes non-admins to their own
   *  participations and audits the admin-elevated read). */
  let threadsGen = 0;
  async function loadThreads(opts?: { page?: number; sort?: string; pageSize?: number }): Promise<void> {
    const gen = ++threadsGen;
    loading.value = true;
    error.value = null;
    try {
      const params = new URLSearchParams({
        channel_kind: 'email',
        view: 'admin',
        sort: opts?.sort ?? 'recent',
        page: String(Math.max(1, opts?.page ?? 1)),
        page_size: String(opts?.pageSize ?? 25),
      });
      const r = await api.get<ThreadListResp>(`/v1/api/conversations?${params.toString()}`);
      if (gen !== threadsGen) return;
      threads.value = r.conversations;
      threadsTotal.value = r.total ?? r.conversations.length;
    } catch (e) {
      if (gen !== threadsGen) return;
      error.value = errOf(e);
    } finally {
      if (gen === threadsGen) loading.value = false;
    }
  }

  async function loadReview(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const r = await api.get<ReviewQueues>('/v1/api/email/review');
      review.value = r;
      badge.value =
        r.counts.awaiting_review + r.counts.unrouted + r.counts.quarantined + r.counts.review_reply;
      reviewReplyCount.value = r.counts.review_reply;
    } catch (e) {
      error.value = errOf(e);
    } finally {
      loading.value = false;
    }
  }

  /** Member surface: the caller's own review-owned replies for the active tab. */
  async function loadMyReplies(reviewed: boolean): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const r = await api.get<{ rows: ActivityRow[] }>(
        `/v1/api/email/replies?reviewed=${reviewed ? 'true' : 'false'}`,
      );
      myReplies.value = r.rows;
    } catch (e) {
      error.value = errOf(e);
    } finally {
      loading.value = false;
    }
  }

  async function loadEndpoints(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const r = await api.get<EndpointListResp>('/v1/api/email/endpoints');
      endpoints.value = r.endpoints;
      emailSlug.value = r.tenant_email_slug;
      domain.value = r.domain;
      outboundLocalpart.value = r.outbound_mailbox_localpart || 'team';
    } catch (e) {
      error.value = errOf(e);
    } finally {
      loading.value = false;
    }
  }

  // ── message content (read-only viewer; fetch-on-view from Sentio) ──
  // Pure fetchers: the viewer owns its own content/loading state, so these just
  // return the parsed EML (headers + decoded bodies + attachment list) or throw.
  async function loadMessageContent(id: string): Promise<EmailMessageContent> {
    return api.get<EmailMessageContent>(`/v1/api/email/messages/${id}/content`);
  }
  async function loadQuarantineContent(id: string): Promise<EmailMessageContent> {
    return api.get<EmailMessageContent>(`/v1/api/email/quarantine/${id}/content`);
  }

  // ── triage actions (throw on failure; callers toast + reload) ──
  async function sendReply(id: string, text: string, html?: string): Promise<void> {
    await api.post(`/v1/api/email/messages/${id}/reply`, html ? { text, html } : { text });
  }
  async function discard(id: string): Promise<void> {
    await api.post(`/v1/api/email/messages/${id}/discard`);
  }
  async function assign(id: string, agentId: string): Promise<void> {
    await api.post(`/v1/api/email/messages/${id}/assign`, { agent_id: agentId });
  }
  /** Attribute a review_reply item to a user (admin-only). Moves it into that
   *  user's review queue; never drives a turn. */
  async function assignOwner(id: string, userId: string): Promise<void> {
    await api.post(`/v1/api/email/messages/${id}/assign-owner`, { user_id: userId });
  }
  /** Clear a review_reply item from the queue (owner-or-admin). */
  async function markReviewed(id: string): Promise<void> {
    await api.post(`/v1/api/email/messages/${id}/reviewed`);
  }
  /** Reopen a reviewed (handled) reply back into the queue (owner-or-admin). */
  async function reopenReply(id: string): Promise<void> {
    await api.post(`/v1/api/email/messages/${id}/reopen`);
  }
  async function acceptQuarantine(
    id: string,
    bindUserId?: string,
  ): Promise<{ ok: boolean; note?: string }> {
    return api.post<{ ok: boolean; note?: string }>(
      `/v1/api/email/quarantine/${id}/accept`,
      bindUserId ? { bind_user_id: bindUserId } : {},
    );
  }
  async function rejectQuarantine(id: string): Promise<void> {
    await api.post(`/v1/api/email/quarantine/${id}/reject`);
  }

  // ── endpoint CRUD (E1.3 API; throws on failure) ──
  async function createEndpoint(input: EndpointCreate): Promise<EmailEndpoint> {
    return api.post<EmailEndpoint>('/v1/api/email/endpoints', input);
  }
  async function updateEndpoint(id: string, patch: EndpointPatch): Promise<EmailEndpoint> {
    return api.patch<EmailEndpoint>(`/v1/api/email/endpoints/${id}`, patch);
  }
  async function deleteEndpoint(id: string): Promise<void> {
    await api.delete(`/v1/api/email/endpoints/${id}`);
  }
  /** Configure the workspace outbound mailbox (localpart + reply handling). */
  async function updateWorkspaceMailbox(patch: WorkspaceMailboxPatch): Promise<void> {
    await api.patch('/v1/api/email/workspace-mailbox', patch);
  }

  return {
    activity, activityTotal, threads, threadsTotal, review, myReplies, endpoints, emailSlug, domain, outboundLocalpart, badge, reviewReplyCount, loading, error,
    refreshBadge, loadActivity, loadThreads, loadReview, loadMyReplies, loadEndpoints,
    loadMessageContent, loadQuarantineContent,
    sendReply, discard, assign, assignOwner, markReviewed, reopenReply, acceptQuarantine, rejectQuarantine,
    createEndpoint, updateEndpoint, deleteEndpoint, updateWorkspaceMailbox,
  };
});
